import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import { UWReviewHeader } from "./UWReviewHeader";
import { UWReviewNotes } from "./UWReviewNotes";
import {
  UWReviewPolicyIdentifiers,
  type UWReviewIdentifier,
} from "./UWReviewPolicyIdentifiers";
import { UWReviewSkeleton } from "./UWReviewSkeleton";
import { AgentStepsList, type UWReviewRecordType } from "./UWReviewStepsLists";

export type UWReviewVariant = "review" | "declined" | "notes";

const VARIANT_COPY: Record<
  UWReviewVariant,
  { title: string; notesLabel: string }
> = {
  review: {
    title: "Underwriting Review Required",
    notesLabel: "Reason for review:",
  },
  declined: {
    title: "Underwriting Declined",
    notesLabel: "Reason for decline:",
  },
  notes: { title: "Underwriting Notes", notesLabel: "Notes:" },
};

export type UWReviewSize = "default" | "small";

const SIZE_STYLE: Record<
  UWReviewSize,
  { text?: number; title?: number; image?: number }
> = {
  default: {},
  small: { text: 16, title: 24, image: 120 },
};

interface UWReviewRequiredProps {
  identifiers: UWReviewIdentifier[];
  notes?: string[];
  recordType?: UWReviewRecordType;
  variant?: UWReviewVariant;
  size?: UWReviewSize;
  isLoading?: boolean;
}

export function UWReviewRequired({
  identifiers,
  notes,
  recordType,
  variant = "review",
  size = "default",
  isLoading = false,
}: UWReviewRequiredProps) {
  const { title, notesLabel } = VARIANT_COPY[variant];
  const {
    text: fontSize,
    title: titleFontSize,
    image: imageSize,
  } = SIZE_STYLE[size];

  if (isLoading) {
    return (
      <UWReviewSkeleton titleFontSize={titleFontSize} imageSize={imageSize} />
    );
  }

  return (
    <Stack>
      <UWReviewHeader
        title={title}
        titleFontSize={titleFontSize}
        imageSize={imageSize}
      />
      <UWReviewPolicyIdentifiers
        identifiers={identifiers}
        fontSize={fontSize}
      />
      <Typography fontWeight={500} fontSize={fontSize}>
        {notesLabel}
      </Typography>
      <UWReviewNotes notes={notes} fontSize={fontSize} />
      {variant !== "notes" && (
        <>
          <Typography fontWeight={500} fontSize={fontSize}>
            Next steps:
          </Typography>
          <AgentStepsList recordType={recordType} fontSize={fontSize} />
        </>
      )}
    </Stack>
  );
}
